//React
import { useCallback, useEffect, useState } from "react";
// Third-party
import { LoadingButton } from "@mui/lab";
import {
  Box,
  Button,
  Divider,
  Link,
  Palette,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import {
  AlertCircle,
  CircleCheck,
  ExternalLink,
  Pencil,
  XCircle,
} from "lucide-react";
// Internal
import { useChatHistory } from "@pages/askDost/hooks/useChatHistory";
import { PATH_CONFIGURATION, PATH_DASHBOARD } from "@routes/paths";
// Types
import { IChatBlock, IConfirmRequest } from "@pages/askDost/types";
import { getOutlinedCardStyle } from "@utils/cssStyles";
import { sendConfirmDecision } from "@pages/askDost/services/askDostService";

interface IConfirmRequestBlockProps {
  block: IChatBlock;
  palette: Palette;
  translate: (text: string, options?: any) => string;
  onEditInstructions?: () => void;
}

export const ConfirmRequestBlock: React.FC<IConfirmRequestBlockProps> = ({
  block,
  palette,
  translate,
  onEditInstructions,
}) => {
  const { setConfirmResolution } = useChatHistory({ translate });
  const [confirmState, setConfirmState] = useState<{
    isLoading: boolean;
    loadingFor: "approve" | "reject" | null;
    error: string | null;
    data: null | IConfirmRequest;
  }>({
    isLoading: false,
    loadingFor: null,
    error: null,
    data: block.confirmResolution ?? null,
  });

  useEffect(() => {
    if (block.confirmResolution && !confirmState.data) {
      setConfirmState({
        isLoading: false,
        loadingFor: null,
        error: null,
        data: block.confirmResolution,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [block.confirmResolution]);

  const handleConfirmDecision = useCallback(
    async (block: IChatBlock, approved: boolean) => {
      setConfirmState({
        isLoading: true,
        loadingFor: approved ? "approve" : "reject",
        error: null,
        data: null,
      });

      try {
        const res = await sendConfirmDecision({
          decision: approved ? "approve" : "reject",
          write_id: block?.write_id!,
        });

        if (res?.status && res.status !== 200) {
          const errorMessage =
            res?.data?.detail === "That proposal is no longer pending."
              ? `no_further_action_required_${
                  block?.confirmTool?.includes("mapping")
                    ? "mapping"
                    : "workflow"
                }`
              : res?.data?.detail || translate("unable_to_send_confirmation");
          setConfirmState({
            isLoading: false,
            loadingFor: null,
            error: errorMessage,
            data: null,
          });
          setConfirmResolution(block.id, res.data);
          return;
        }
        setConfirmState({
          isLoading: false,
          loadingFor: null,
          error: null,
          data: res.data,
        });
        setConfirmResolution(block.id, res.data);
      } catch (error) {
        console.error("Error sending confirmation:", error);
        const errorMessage =
          error instanceof Error
            ? error.message
            : translate("unable_to_send_confirmation");
        setConfirmState({
          isLoading: false,
          loadingFor: null,
          error: errorMessage,
          data: null,
        });
      }
    },
    [setConfirmResolution, translate],
  );

  return (
    <Paper
      elevation={0}
      sx={{
        ...getOutlinedCardStyle(palette, 2),
        overflow: "hidden",
        mb: 2,
        minWidth: "100%",
      }}
    >
      {/* Header */}
      <Box
        sx={{
          px: 2,
          py: 1.25,
          backgroundColor:
            palette.mode === "dark" ? palette.primary.dark : "#EAF5FF",
          borderBottom: `1px solid ${palette.text.borderLine}`,
        }}
      >
        <Typography fontWeight={600}>{block.confirmSummary}</Typography>
      </Box>

      {/* Body */}
      <Stack spacing={2} sx={{ p: 2 }}>
        {block.confirmDetails?.map((item) => (
          <Box key={item.label}>
            <Typography
              variant="body2"
              color={palette.text.label}
              sx={{ display: "block" }}
            >
              {item.label}
            </Typography>

            <Typography variant="body2">{item.value}</Typography>
          </Box>
        ))}
      </Stack>

      <Divider />

      {/* Footer */}
      <Stack
        direction="row"
        justifyContent="flex-end"
        spacing={1.5}
        sx={{ p: 2 }}
      >
        {confirmState.data ? (
          <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
            {confirmState.data.status === "expired" ? (
              <Typography fontWeight={600} color={palette.text.label}>
                {translate("proposal_expired")}
              </Typography>
            ) : confirmState.data.status === "failed" ? (
              <Stack
                direction="row"
                spacing={1}
                sx={{ alignItems: "flex-start" }}
              >
                <AlertCircle
                  size={18}
                  color={palette.error.main}
                  style={{ marginTop: "2px", flexShrink: 0 }}
                />
                <Typography fontWeight={600} color="error.dark">
                  {translate("proposal_failed")}
                </Typography>
              </Stack>
            ) : !confirmState.data.is_error ? (
              confirmState.data.decision === "approve" ? (
                <Link
                  href={
                    block.confirmTool?.includes("mapping")
                      ? PATH_CONFIGURATION.general.inputCSVMapping
                      : PATH_DASHBOARD.general.workflow
                  }
                  underline="hover"
                  color="success.dark"
                  sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 0.5,
                    fontWeight: 600,
                  }}
                >
                  {translate(
                    confirmState.data.decision === "approve"
                      ? "approved"
                      : "rejected",
                    {
                      confirmTool: translate(
                        block.confirmTool === "create_input_mapping"
                          ? "mapping"
                          : "workflow",
                      ),
                    },
                  )}
                  <ExternalLink size={16} />
                </Link>
              ) : (
                <>
                  <Typography fontWeight={600} color="error.dark">
                    {translate("rejected", {
                      confirmTool: translate(
                        block.confirmTool === "create_input_mapping"
                          ? "mapping"
                          : "workflow",
                      ),
                    })}
                  </Typography>
                  <Button
                    variant="outlined"
                    size="small"
                    endIcon={<Pencil color={palette.primary.main} size={16} />}
                    onClick={() => onEditInstructions?.()}
                  >
                    <Typography variant="body2">
                      {translate("edit_instructions")}
                    </Typography>
                  </Button>
                </>
              )
            ) : (
              <Stack
                direction="row"
                spacing={1}
                sx={{ alignItems: "flex-start" }}
              >
                <AlertCircle
                  size={18}
                  color={palette.error.main}
                  style={{ marginTop: "2px", flexShrink: 0 }}
                />
                <Typography fontWeight={600} color="error.dark">
                  {confirmState.data.results[0]?.message}
                </Typography>
              </Stack>
            )}
          </Stack>
        ) : (
          <Stack
            direction="row"
            spacing={1.5}
            sx={{ justifyContent: "flex-end", alignItems: "center" }}
          >
            {confirmState.error && (
              <Stack
                direction="row"
                spacing={1}
                sx={{ alignItems: "flex-start" }}
              >
                <AlertCircle
                  size={18}
                  color={palette.error.main}
                  style={{ marginTop: "2px", flexShrink: 0 }}
                />
                <Typography fontWeight={600} color="error.dark">
                  {translate(confirmState.error)}
                </Typography>
              </Stack>
            )}
            <LoadingButton
              variant="outlined"
              size="small"
              onClick={() => handleConfirmDecision(block, false)}
              color="error"
              loading={
                confirmState.isLoading && confirmState.loadingFor === "reject"
              }
              disabled={confirmState.isLoading || !!confirmState.error}
              sx={{
                minWidth: 90,
                border: `1px solid ${palette.error.dark}`,
                borderRadius: 1,
                color: palette.error.dark,
                fontSize: "14px",
              }}
              endIcon={
                <XCircle
                  size={16}
                  color={!!confirmState.error ? undefined : palette.error.dark}
                />
              }
            >
              {translate("reject")}
            </LoadingButton>

            <LoadingButton
              variant="contained"
              size="small"
              onClick={() => handleConfirmDecision(block, true)}
              loading={
                confirmState.isLoading && confirmState.loadingFor === "approve"
              }
              disabled={confirmState.isLoading || !!confirmState.error}
              endIcon={<CircleCheck size={16} />}
              sx={{
                minWidth: 90,
                fontSize: "14px",
                color: "#fff",
                borderRadius: 1,
                boxShadow: "none",
                backgroundColor: palette.success.dark,
                ":hover": {
                  backgroundColor: palette.success.darker,
                },
              }}
            >
              {translate("accept")}
            </LoadingButton>
          </Stack>
        )}
      </Stack>
    </Paper>
  );
};
